import React, { useState, useEffect } from 'react'
import { Button, Dimmer, Loader, Message, Modal } from 'semantic-ui-react'
import Data, { IDataset } from '../../api/Data'
import Auth from '../../utils/Auth'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import './LinkDatasetsModal.scss'

const LinkDatasetsModal: React.FC<LinkDatasetsModalProps> = ({ isOpen, onClose, onSelect }) => {
  // States
  const [datasets, setDatasets] = useState<IDataset[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDatasetId, setSelectedDatasetId] = useState<number | null>(null)
  const [selectedDatasetTitle, setSelectedDatasetTitle] = useState<string>('')

  useEffect(() => {
    if (isOpen) {
      fetchDatasets()
    } else {
      resetSelection()
    }
  }, [isOpen])

  const fetchDatasets = async () => {
    setLoading(true)
    setError(null)

    const { teamId } = Auth.getPayLoad() || {}

    if (!teamId) {
      setError('Unable to find team ID. Please refresh the page and try again!')
      setLoading(false)
      return
    }

    try {
      const { data, error } = await Data.getTeamDatasets(teamId)

      if (error) {
        setError('An error occurred. Please try again later.')
        console.error('Error fetching datasets:', error)
        return
      }

      setDatasets(data?.datasets || [])
    } catch (error) {
      setError('An error occurred. Please try again later.')
      console.error('Failed to load datasets:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSelectDataset = (datasetId: number, datasetTitle: string) => {
    setSelectedDatasetId(datasetId)
    setSelectedDatasetTitle(datasetTitle)
  }

  const handleLinkDataset = () => {
    if (selectedDatasetId && selectedDatasetTitle) {
      onSelect(selectedDatasetId, selectedDatasetTitle)
      onClose()
    }
  }

  const resetSelection = () => {
    setSelectedDatasetId(null)
    setSelectedDatasetTitle('')
  }

  return (
    <Modal open={isOpen} onClose={onClose} size="large" className="link-datasets-modal">
      <Modal.Header className="link-datasets-modal__header">Select Dataset</Modal.Header>
      <Modal.Content className="link-datasets-modal__content">
        {loading ? (
          <Dimmer active={loading}>
            <Loader size="medium" className="link-datasets-modal__loader">
              <span className="link-datasets-modal__loader-text">Loading datasets...</span>
            </Loader>
          </Dimmer>
        ) : error ? (
          <Message negative className="link-datasets-modal__error">
            <Message.Header>{error}</Message.Header>
          </Message>
        ) : datasets.length > 0 ? (
          <Table size="small" className="link-datasets-modal__table">
            <TableHead>
              <TableRow className="link-datasets-modal__table-header">
                <TableCell className="link-datasets-modal__table-header-cell">Title</TableCell>
                <TableCell className="link-datasets-modal__table-header-cell">Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datasets.map((dataset: IDataset, i: number) => (
                <TableRow
                  key={i}
                  className={`link-datasets-modal__table-row ${
                    selectedDatasetId === dataset.id ? 'link-datasets-modal__table-row--selected' : ''
                  }`}
                  onClick={() => handleSelectDataset(dataset.id as number, dataset.title)}
                >
                  <TableCell className="link-datasets-modal__table-cell">{dataset.title}</TableCell>
                  <TableCell className="link-datasets-modal__table-cell">{dataset.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="link-datasets-modal__no-datasets">
            <p>No datasets have been created yet.</p>
          </div>
        )}
      </Modal.Content>
      <Modal.Actions className="link-datasets-modal__actions">
        <Button onClick={onClose}>Cancel</Button>
        <Button primary disabled={!selectedDatasetId} onClick={handleLinkDataset}>
          Link
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

interface LinkDatasetsModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (datasetId: number, datasetTitle: string) => void
}

export default LinkDatasetsModal
